import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import React from 'react';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Animated, { ZoomIn, useReducedMotion } from 'react-native-reanimated';

import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { useThemePalette } from '@/config/theme';
import { useAchievements } from '@/features/games/rewards';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

type IoniconName = keyof typeof Ionicons.glyphMap;

const ACH_ICONS: Record<string, IoniconName> = {
  first_squad: 'shirt',
  first_captain: 'star',
  first_challenge: 'flash',
  first_prediction: 'help-circle',
  predict_correct: 'checkmark-done',
  first_acca: 'layers',
  acca_jackpot: 'trophy',
  survivor_join: 'shield-half',
  survivor_run: 'shield-checkmark',
  spin_master: 'sync-circle',
  follow_five: 'heart',
  streak_seven: 'flame',
  score_first: 'football',
  score_exact: 'locate',
  prize_ticket: 'ticket',
  prize_winner: 'gift',
  coin_collector: 'cash',
  friend_league: 'people',
};

/** Prestige badges get a gold ring instead of the default cyan, for variety. */
const GOLD_BADGES = new Set([
  'acca_jackpot',
  'survivor_run',
  'streak_seven',
  'prize_ticket',
  'prize_winner',
  'coin_collector',
]);

/** One badge tile: earned = glowing gradient ring with a crisp icon; locked = dimmed disc with a tiny padlock. */
function BadgeTile({
  icon,
  earned,
  gold,
  index,
  reduce,
}: {
  icon: IoniconName;
  earned: boolean;
  gold: boolean;
  index: number;
  reduce: boolean;
}) {
  const palette = useThemePalette();
  const ring = gold
    ? {
        from: palette.captain + '3D',
        to: palette.captain + '14',
        border: palette.captain + '66',
        glow: palette.captain,
        icon: palette.captain,
      }
    : {
        from: palette.voltGlassStrong,
        to: palette.voltGlass,
        border: palette.voltLine,
        glow: palette.volt,
        icon: palette.voltBright,
      };

  return (
    <Animated.View entering={reduce ? undefined : ZoomIn.delay(index * 40).springify().damping(14)}>
      {earned ? (
        <LinearGradient
          colors={[ring.from, ring.to]}
          start={{ x: 0.2, y: 0 }}
          end={{ x: 0.8, y: 1 }}
          style={{
            height: 46,
            width: 46,
            borderRadius: 23,
            alignItems: 'center',
            justifyContent: 'center',
            borderWidth: 1,
            borderColor: ring.border,
            shadowColor: ring.glow,
            shadowOpacity: 0.55,
            shadowRadius: 7,
            shadowOffset: { width: 0, height: 0 },
            elevation: 6,
          }}
        >
          <Ionicons name={icon} size={22} color={ring.icon} />
        </LinearGradient>
      ) : (
        <View
          className="h-11 w-11 items-center justify-center rounded-full border border-line"
          style={{ backgroundColor: palette.deep, opacity: 0.55 }}
        >
          <Ionicons name={icon} size={19} color={palette.inactive} />
          <View
            className="absolute h-4 w-4 items-center justify-center rounded-full"
            style={{ bottom: -1, right: -1, backgroundColor: palette.deep, borderWidth: 1, borderColor: palette.line }}
          >
            <Ionicons name="lock-closed" size={9} color={palette.inactive} />
          </View>
        </View>
      )}
    </Animated.View>
  );
}

/**
 * A glanceable badges wall on the profile: every achievement as a tile, earned
 * ones lit, locked ones dimmed. Taps through to the full Rewards screen.
 */
export function BadgesShowcase() {
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const router = useRouter();
  const reduce = useReducedMotion();
  const achievements = useAchievements();

  const data = achievements.data ?? [];
  if (data.length === 0) return null;
  const unlocked = data.filter((a) => a.unlocked_at != null).length;

  return (
    <Pressable accessibilityRole="button" onPress={() => router.push('/rewards' as never)} className="active:opacity-90">
      <Card className="mx-4 mb-5 px-4 py-4">
        <View className="mb-3 flex-row items-center justify-between">
          <AppText variant="heading" weight="bold" className="text-ink">
            {t('profile.badges')}
          </AppText>
          <AppText variant="caption" weight="bold" tabular className="text-volt-bright">
            {t('profile.badgesCount', {
              unlocked: formatNumber(unlocked, numeralOptions),
              total: formatNumber(data.length, numeralOptions),
            })}
          </AppText>
        </View>
        <View className="flex-row flex-wrap" style={{ gap: 10 }}>
          {data.map((a, index) => (
            <BadgeTile
              key={a.key}
              icon={ACH_ICONS[a.key] ?? 'ribbon'}
              earned={a.unlocked_at != null}
              gold={GOLD_BADGES.has(a.key)}
              index={index}
              reduce={reduce}
            />
          ))}
        </View>
      </Card>
    </Pressable>
  );
}
